import Link from 'next/link'
import {Fragment, memo, useState} from 'react'

import {IconArrowRightRound, IconQuestionMark} from '@defign/asset'
import classNames from 'classnames/bind'

import LoadingBar from '$components/common/LoadingBar'
import {sendClickNlog} from '$utils/nlog'

import Styles from './InsuranceDetailInfoList.module.scss'

import type {ReactNode, SyntheticEvent} from 'react'
import type {UrlObject} from 'url'

const cx = classNames.bind(Styles)

// TODO : 타 상세보기 컴포넌트 작업 후 제거
export interface Item {
    title: string
    value: string
    detail?: string
    type?: string
    isOn?: boolean
    layerComponent?: ({show, onClose}: {show: boolean; onClose: () => void}) => JSX.Element
}

interface InsuranceDetailInfoListProps {
    items: Item[]
}

const InsuranceDetailInfoList = memo(({items}: InsuranceDetailInfoListProps) => {
    const [showLayer, setShowLayer] = useState(false)

    return (
        <ul className={cx('article')}>
            {items.map((item, key) => {
                const {layerComponent: LayerComponent} = item
                return (
                    <li key={key} className={cx('item')}>
                        <strong className={cx('title')}>
                            {item.type === 'link' ? (
                                <a href="" className={cx('link')}>
                                    {item.title}
                                    <IconArrowRightRound width={7} height={11} fill="#929294" />
                                </a>
                            ) : (
                                <>
                                    {item.title}
                                    {item.type === 'button' && (
                                        <button
                                            type="button"
                                            className={cx('button')}
                                            onClick={() => setShowLayer(true)}
                                        >
                                            <IconQuestionMark width={14} height={14} fill="#929294" />
                                            <span className="blind">자세히 보기</span>
                                        </button>
                                    )}
                                    {LayerComponent && (
                                        <LayerComponent show={showLayer} onClose={() => setShowLayer(false)} />
                                    )}
                                </>
                            )}
                        </strong>
                        {/* [D] 실효/만기/소멸 일때 on 클래스 적용 부탁드립니다. */}
                        <span className={cx('value', {on: item.isOn})}>
                            {item.value}
                            {item.detail && <span className={cx('sub')}>{item.detail}</span>}
                        </span>
                    </li>
                )
            })}
        </ul>
    )
})

export const InsuranceDetailInfoItemWrapper = ({children}: {children: React.ReactNode}) => {
    return <ul className={cx('article')}>{children}</ul>
}

interface InsuranceDetailInfoItemValueProps {
    value?: string | string[]
    detail?: string
    isOn?: boolean
    isLoading?: boolean
}
const InsuranceDetailInfoItemValue = memo(({value = '', detail, isOn}: InsuranceDetailInfoItemValueProps) => {
    return (
        <span className={cx('value', {on: isOn})}>
            {typeof value === 'string' ? (
                <>{value}</>
            ) : (
                value.map((str, index) => (
                    <Fragment key={index}>
                        {str}
                        {index < value.length - 1 ? <br /> : null}
                    </Fragment>
                ))
            )}

            {detail && <span className={cx('sub')}>{detail}</span>}
        </span>
    )
})

interface InsuranceDetailInfoItemProps extends InsuranceDetailInfoItemValueProps {
    title: string
}
export const InsuranceDetailInfoItem = memo(({title, value, detail, isOn, isLoading}: InsuranceDetailInfoItemProps) => {
    if (!value && !isLoading) {
        return null
    }

    return (
        <li className={cx('item')}>
            <strong className={cx('title')}>{title}</strong>
            {isLoading ? (
                <span className={cx('loading')}>
                    <LoadingBar size="size6" />
                </span>
            ) : (
                <InsuranceDetailInfoItemValue value={value} detail={detail} isOn={isOn} />
            )}
        </li>
    )
})

interface InsuranceDetailInfoLinkItemProps extends InsuranceDetailInfoItemProps {
    href: string | UrlObject
    nClickCode?: string
}
export const InsuranceDetailInfoLinkItem = memo(
    ({href, title, value, detail, isLoading, isOn, nClickCode}: InsuranceDetailInfoLinkItemProps) => {
        if (!value && !isLoading) {
            return null
        }

        function handleClick() {
            if (nClickCode) {
                sendClickNlog(nClickCode)
            }
        }

        return (
            <li className={cx('item')}>
                <strong className={cx('title')}>
                    <Link href={href} className={cx('link')} onClick={handleClick}>
                        {title}
                        <IconArrowRightRound width={7} height={11} fill="#929294" />
                    </Link>
                </strong>
                {isLoading ? (
                    <span className={cx('loading')}>
                        <LoadingBar size="size6" />
                    </span>
                ) : (
                    <InsuranceDetailInfoItemValue value={value} detail={detail} isOn={isOn} />
                )}
            </li>
        )
    },
)

interface InsuranceDetailInfoButtonItemProps extends InsuranceDetailInfoItemProps {
    onClick: (e: SyntheticEvent) => void
    children?: ReactNode
}
export const InsuranceDetailInfoButtonItem = ({
    title,
    value,
    detail,
    isOn,
    onClick,
    children,
}: InsuranceDetailInfoButtonItemProps) => {
    if (!value) {
        return null
    }

    return (
        <li className={cx('item')}>
            <strong className={cx('title')}>
                {title}
                <button type="button" className={cx('button')} onClick={onClick}>
                    <IconQuestionMark width={14} height={14} fill="#929294" />
                    <span className="blind">자세히 보기</span>
                </button>
            </strong>
            <InsuranceDetailInfoItemValue value={value} detail={detail} isOn={isOn} />
            {children}
        </li>
    )
}

export default InsuranceDetailInfoList
